import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Link,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import api from '../../utils/api';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuth } from '../../context/AuthContext';
import useComponentStyles from '../../theme/useComponentStyles';
import LegalDocumentModal from '../Legal/LegalDocumentModal';

function Login({ skipRoleSelection = false }) {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    deviceId: '',
  });
  const [selectedJourney, setSelectedJourney] = useState(skipRoleSelection ? 'couple' : null);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [verificationStep, setVerificationStep] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [userId, setUserId] = useState(null);
  const [rememberDevice, setRememberDevice] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [legalModal, setLegalModal] = useState({ open: false, fileName: null, title: '' });
  const [legalDocs, setLegalDocs] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const styles = useComponentStyles();
  const theme = useTheme();
  const { user, loading: authLoading, login } = useAuth();

  useEffect(() => {
    const loadLegalDocs = async () => {
      try {
        const response = await api.get('/api/legal', { params: { userType: 'user' } });
        setLegalDocs(response.data?.documents || []);
      } catch {
        setLegalDocs([]);
      }
    };

    loadLegalDocs();
  }, []);

  const getLegalDoc = (...fileNames) => legalDocs.find((doc) => fileNames.includes(doc.fileName));
  const legalLinks = [
    getLegalDoc('general-terms-couples'),
    getLegalDoc('privacy-policy'),
    getLegalDoc('cookie-policy', 'cookies-policy'),
  ].filter(Boolean);
  const getLegalPath = (fileName) => {
    if (fileName === 'general-terms-couples') return '/general-terms-couples';
    if (fileName === 'privacy-policy') return '/privacy-policy';
    if (fileName === 'cookie-policy' || fileName === 'cookies-policy') return '/cookie-policy';
    return '/privacy-policy';
  };

  useEffect(() => {
    if (skipRoleSelection) {
      setSelectedJourney('couple');
    }
  }, [skipRoleSelection]);

  // Redirect to dashboard when already logged in
  useEffect(() => {
    const token = localStorage.getItem('token');
    const userType = localStorage.getItem('userType');
    if (!loading && token && user?.role) {
      if (userType === 'supplier' && user.role === 'supplier') {
        navigate('/supplier/dashboard', { replace: true });
      } else if (userType !== 'supplier' && userType !== 'admin') {
        navigate('/dashboard', { replace: true });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [authLoading, user, navigate]);

  // Clear tokens only when not logged in (preserve session when user lands here by accident)
  useEffect(() => {
    const token = localStorage.getItem('token');
    const userType = localStorage.getItem('userType');
    const hasValidSession = token && (userType === 'supplier' || userType === 'admin' || (user && user.role));
    if (!authLoading && !location.state?.from && !hasValidSession) {
      localStorage.removeItem('token');
      localStorage.removeItem('userType');
      delete api.defaults.headers.common['x-auth-token'];
    }
  }, [authLoading, location, user]);

  useEffect(() => {
    // Check for saved device ID
    const deviceId = localStorage.getItem('deviceId');
    if (deviceId) {
      setFormData(prev => ({
        ...prev,
        deviceId
      }));
    }
  }, []);

  const handleChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // First check if this is a supplier account
      try {
        const checkUserResponse = await api.post('/api/auth/check-user-type', { 
          email: formData.email 
        });
        
        if (checkUserResponse.data.userType === 'supplier') {
          navigate('/supplier/login', { 
            state: { 
              email: formData.email,
              message: 'Please use the supplier login page for your account type'
            } 
          });
          return;
        }
      } catch (checkError) {}

      const response = await login(formData.email, formData.password, false, formData.deviceId);
      
      // Check if verification is required
      if (response.requiresVerification) {
        setUserId(response.userId);
        setVerificationStep(true);
        setError(response.message); // This will show as an informative message
        return;
      }

      // Check if email verification is required
      if (response.requiresEmailVerification) {
        setUserId(response.userId);
        setVerificationStep(true);
        setError(response.message); // This will show as an informative message
        return;
      }

      // If login was successful, navigate to the appropriate dashboard based on user type
      if (response.user && response.user.role === 'supplier') {
        navigate('/supplier/dashboard');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      // Check for email verification requirement
      if (err.response?.data?.requiresEmailVerification) {
        setUserId(err.response.data.userId);
        setVerificationStep(true);
        setError(''); // Clear error - don't show the initial message
        return;
      }

      // Check if platform is closed/maintenance
      if (err.response?.status === 503 && err.response?.data?.maintenance) {
        // Platform is closed - PlatformStatusCheck should handle this, but just in case
        setError({
          message: err.response.data.message,
          maintenance: true
        });
        return;
      }

      // Check if this is a pre-registered user error
      if (err.response?.data?.isPreRegistered) {
        setError({
          message: err.response.data.message,
          isPreRegistered: true
        });
        return;
      }

      if (err.response?.data?.message === 'Invalid email or password') {
        // Check if this might be a supplier account
        try {
          const checkUserResponse = await api.post('/api/auth/check-user-type', { 
            email: formData.email 
          });
          
          if (checkUserResponse.data.userType === 'supplier') {
            setError('This appears to be a supplier account. Please use the supplier login page.');
            return;
          }
        } catch (checkError) {
          // If the check fails, show the original error
        }
      }

      setError(err.response?.data?.message || 'Failed to sign in');
    } finally {
      setLoading(false);
    }
  };

  const handleVerificationSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Determine which verification endpoint to call based on the verification type
      const isEmailVerification = error && error.includes('Email verification required');
      const endpoint = isEmailVerification ? '/api/auth/verify-email' : '/api/auth/verify-login';

      const requestBody = isEmailVerification 
        ? { userId, code: verificationCode }
        : { userId, code: verificationCode, rememberDevice, deviceName: navigator.userAgent };

      const response = await api.post(endpoint, requestBody);

      // Store authentication data
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('userId', response.data.user._id);
      localStorage.setItem('userType', 'user');

      // Store device ID if remember device was enabled (only for login verification)
      if (!isEmailVerification && response.data.deviceId) {
        localStorage.setItem('deviceId', response.data.deviceId);
      }

      // Store wedding ID if available
      if (response.data.wedding) {
        localStorage.setItem('weddingId', response.data.wedding._id);
      }

      // Set token in API headers
      api.defaults.headers.common['x-auth-token'] = response.data.token;

      // Clear verification state
      setVerificationStep(false);
      setVerificationCode('');

      // Navigate to appropriate dashboard based on user type
      if (response.data.user && response.data.user.role === 'supplier') {
        navigate('/supplier/dashboard', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Error verifying code');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await api.post('/api/auth/resend-verification', {
        userId,
        isLogin: true // Always true for login verification
      });

      if (response.data.emailSent !== false) {
        setError('A new verification code has been sent to your email');
      } else {
        setError('Failed to send verification email. Please try again or contact support.');
      }
    } catch (error) {
      setError(
        error.response?.data?.message ||
        error.message ||
        'Failed to resend verification code'
      );
    } finally {
      setLoading(false);
    }
  };

  if (!skipRoleSelection && selectedJourney !== 'couple') {
    return (
      <Box sx={{ 
        minHeight: '100vh',
        backgroundImage: `url(${process.env.PUBLIC_URL}/img/brand-bg.jpg)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        backgroundRepeat: 'no-repeat',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4
      }}>
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography
            variant="body1"
            component="h1"
            align="center"
            sx={{ fontWeight: 500, color: 'text.primary', mb: 1 }}
          >
            Welcome to
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <img src={`${process.env.PUBLIC_URL}/img/logo.svg`} alt="Idotoo Logo" style={{ height: '40px' }} />
          </Box>
        </Box>

        <Box
          sx={{
            maxWidth: '400px',
            width: '100%',
            mx: 2
          }}
        >
          <Typography
            variant="body1"
            align="center"
            sx={{ color: 'text.secondary', mb: 3 }}
          >
            Let's get you to the right place.
          </Typography>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 2,
              border: '1px solid rgba(0, 0, 0, 0.08)',
              backgroundColor: 'background.paper'
            }}
          >
            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                setSelectedJourney('couple');
                setError('');
              }}
              sx={{ py: 1.5, textTransform: 'none', fontSize: '1rem', mb: 1.5 }}
            >
              I'm planning my wedding
            </Button>
            <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
              Discover suppliers, manage your timeline and plan with your partner
            </Typography>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              border: '1px solid rgba(0, 0, 0, 0.08)',
              backgroundColor: 'background.paper'
            }}
          >
            <Button
              fullWidth
              component={RouterLink}
              to="/supplier/login"
              variant="contained"
              sx={{ py: 1.5, textTransform: 'none', fontSize: '1rem', mb: 1.5 }}
            >
              I'm a supplier/planner
            </Button>
            <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
              Showcase your business, manage enquiries and grow your bookings
            </Typography>
          </Paper>
        </Box>

        {legalLinks.length > 0 && (
          <Typography
            variant="caption"
            sx={{ mt: 3, color: 'text.secondary', textAlign: 'center', px: 2 }}
          >
            By continuing, you agree to our{' '}
            {legalLinks.map((doc, index) => (
              <React.Fragment key={doc.fileName}>
                <Link
                  component={RouterLink}
                  to={getLegalPath(doc.fileName)}
                  sx={{ fontSize: 'inherit', color: 'text.secondary', textDecoration: 'underline' }}
                >
                  {doc.title}
                </Link>
                {index < legalLinks.length - 2 ? ', ' : index === legalLinks.length - 2 ? ' and ' : '.'}
              </React.Fragment>
            ))}
          </Typography>
        )}

        <LegalDocumentModal
          open={legalModal.open}
          onClose={() => setLegalModal({ open: false, fileName: null, title: '' })}
          fileName={legalModal.fileName}
          title={legalModal.title}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh',
      backgroundImage: `url(${process.env.PUBLIC_URL}/img/brand-bg.jpg)`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'fixed',
      backgroundRepeat: 'no-repeat',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      py: 4
    }}>
      {!skipRoleSelection && (
        <Box
          sx={{
            width: '100%',
            maxWidth: '400px',
            mx: 2,
            mb: 1.5
          }}
        >
          <Button
            variant="text"
            onClick={() => {
              setSelectedJourney(null);
              setError('');
            }}
            startIcon={<ArrowBackIcon fontSize="small" />}
            sx={{ textTransform: 'none', p: 0, minWidth: 'auto' }}
          >
            Back
          </Button>
        </Box>
      )}

      {/* Logo */}
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          mb: 4
        }}
      >
        <img src={`${process.env.PUBLIC_URL}/img/logo.svg`} alt="Idotoo Logo" style={{ height: '40px' }} />
      </Box>

      {/* Login Card */}
      <Paper 
        elevation={0}
        sx={{ 
          backgroundColor: 'background.paper',
          maxWidth: '400px',
          width: '100%',
          mx: 2,
          overflow: 'hidden',
          border: '1px solid rgba(0, 0, 0, 0.08)'
        }}
      >
        <Box sx={{ p: 4 }}>
          <Typography 
            variant="h5" 
            component="h2" 
            align="center" 
            gutterBottom
            sx={{ 
              fontWeight: 500,
              color: 'text.primary',
              mb: 3
            }}
          >
            Login to start planning
          </Typography>

          {location.state?.message && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {location.state.message}
            </Alert>
          )}
              
          {error && (
            <Alert 
              severity={typeof error === 'object' && error.isPreRegistered ? 'info' : 'error'}
              icon={false}
              sx={{ 
                mb: 3,
                ...(typeof error === 'object' && error.isPreRegistered ? {
                  backgroundColor: '#fff3f8',
                  border: '1px solid #ffb3d9',
                  borderRadius: 2,
                  '& .MuiAlert-message': {
                    width: '100%',
                    textAlign: 'center'
                  }
                } : {})
              }}
            >
              <Typography variant="body2" sx={{ 
                textAlign: typeof error === 'object' && error.isPreRegistered ? 'center' : 'left'
              }}>
                {typeof error === 'object' && error.isPreRegistered ? (
                  <>💕 {error.message} 💕</>
                ) : (
                  typeof error === 'object' ? error.message : error
                )}
              </Typography>
              {typeof error === 'object' && error.isPreRegistered && (
                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <Button
                    variant="outlined"
                    href="https://idotoo.ai"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ 
                      textTransform: 'none',
                      borderColor: '#e91e63',
                      color: '#e91e63',
                      '&:hover': {
                        borderColor: '#c2185b',
                        backgroundColor: '#fff3f8'
                      }
                    }}
                  >
                    Visit idotoo.ai
                  </Button>
                </Box>
              )}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              placeholder="Email"
              type="email"
              value={formData.email}
              onChange={handleChange('email')}
              margin="normal"
              required
              variant="outlined"
              autoComplete="email"
              autoFocus
              sx={{ 
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fff'
                }
              }}
            />
            <TextField
              fullWidth
              placeholder="Password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange('password')}
              margin="normal"
              required
              variant="outlined"
              autoComplete="current-password"
              sx={{ 
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fff'
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              sx={{ 
                py: 1.5,
                mb: 2,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 500,
                color: 'rgba(255, 255, 255, 1)',
                border: '1px solid #000'
              }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Sign in'}
            </Button>

            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Link 
                component={RouterLink} 
                to="/forgot-password" 
                underline="hover" 
                sx={{ 
                  color: 'text.secondary',
                  fontSize: '0.875rem'
                }}
              >
                Forgotten password?
              </Link>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Sign Up Card */}
      <Paper 
        elevation={0}
        sx={{ 
          backgroundColor: 'secondary.dark',
          maxWidth: '400px',
          width: '100%',
          mx: 2,
          mt: 2,
          overflow: 'hidden',
          border: '1px solid rgba(0, 0, 0, 0.08)'
        }}
      >
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography 
            variant="h5" 
            component="h3"
            sx={{ 
              fontWeight: 600,
              color: 'text.primary',
              mb: 1
            }}
          >
            No account?
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'black',
              mb: 3
            }}
          >
            No problem, sign up today its completely free!
          </Typography>
          <Button
            component={RouterLink}
            to="/couple/register"
            variant="contained"
            fullWidth
              sx={{ 
                py: 1.5,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 500,
                backgroundColor: 'background.paper',
                color: 'text.primary',
              '&:hover': {
                backgroundColor: 'grey.100'
              }
            }}
          >
            Start my planning journey
          </Button>
        </Box>
      </Paper>

      {/* Supplier Login Link */}
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Are you a supplier?{' '}
          <Link 
            component={RouterLink} 
            to="/supplier/login" 
            underline="hover" 
            sx={{ 
              color: 'black',
              fontWeight: 500
            }}
          >
            Login here
          </Link>
        </Typography>
      </Box>

      {legalLinks.length > 0 && (
        <Typography
          variant="caption"
          sx={{ mt: 2, color: 'text.secondary', textAlign: 'center', px: 2 }}
        >
          By continuing, you agree to our{' '}
          {legalLinks.map((doc, index) => (
            <React.Fragment key={doc.fileName}>
              <Link
                component={RouterLink}
                to={getLegalPath(doc.fileName)}
                sx={{ fontSize: 'inherit', color: 'text.secondary', textDecoration: 'underline' }}
              >
                {doc.title}
              </Link>
              {index < legalLinks.length - 2 ? ', ' : index === legalLinks.length - 2 ? ' and ' : '.'}
            </React.Fragment>
          ))}
        </Typography>
      )}

      {/* Verification Dialog */}
      <Dialog 
        open={verificationStep} 
        maxWidth="xs" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden'
          }
        }}
      >
        <Box
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
            px: 3,
            py: 2.5,
            textAlign: 'center'
          }}
        >
          <DialogTitle sx={{ color: 'white', p: 0, pb: 0, borderBottom: 'none' }}>
            <Typography variant="h5" sx={{ fontWeight: 500, color: 'white' }}>
              Enter Verification Code
            </Typography>
          </DialogTitle>
        </Box>
        <DialogContent sx={{ px: 3, pt: 3, pb: 2 }}>
          <TextField
            fullWidth
            label="Verification Code"
            value={verificationCode}
            onChange={(e) => {
              // Only allow digits and limit to 6 characters
              const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
              setVerificationCode(value);
            }}
            variant="outlined"
            placeholder="000000"
            inputProps={{
              maxLength: 6,
              inputMode: 'numeric',
              pattern: '[0-9]*',
              style: { 
                textAlign: 'center',
                fontSize: '24px',
                letterSpacing: '8px',
                fontWeight: 500,
                padding: '14px 0',
                height: 'auto',
                lineHeight: 'normal'
              }
            }}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                alignItems: 'center',
                '& input': {
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                },
                '& fieldset': {
                  borderWidth: 2
                }
              },
              '& .MuiInputBase-input': {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: 'auto',
                py: 1.5
              }
            }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={rememberDevice}
                onChange={(e) => setRememberDevice(e.target.checked)}
                color="primary"
              />
            }
            label="Remember this device for 30 days"
            sx={{ mb: 2 }}
          />
          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          )}
          <Button
            variant="text"
            color="primary"
            onClick={handleResendCode}
            disabled={loading}
            sx={{ 
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            Resend Code
          </Button>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            variant="contained"
            onClick={handleVerificationSubmit}
            disabled={loading || !verificationCode.trim()}
            fullWidth
            size="large"
            sx={{
              ...styles.buttons.primary,
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 500
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify'}
          </Button>
        </DialogActions>
      </Dialog>

      <LegalDocumentModal
        open={legalModal.open}
        onClose={() => setLegalModal({ open: false, fileName: null, title: '' })}
        fileName={legalModal.fileName}
        title={legalModal.title}
      />
    </Box>
  );
}

export default Login; 